import React, { useEffect, useMemo, useState } from "react";
import { apiClient } from "../services/apiClient";
import "../styles/AdminSettingsPage.css";
import { useConfirm } from "../providers/ConfirmProvider";
import { useI18n } from "../providers/I18nProvider";
import { TabBar, FilterBar, SearchInput, EmptyState } from "../components/ListToolbar";

// Servis-servis entegrasyon API anahtarları yönetim ekranı.
// Backend: /api/admin/integration-api-keys (list/rotate/revoke).
// "raw_key" sadece üretildiğinde ve aktif anahtar için döner; bir kere
// kopyaladıktan sonra DB'den siliniyor (rotate veya revoke ile temizlenir).

const formatDateTime = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString("tr-TR");
  } catch {
    return String(value);
  }
};

const KeyRow = ({ row, onCopy, onRevoke, copyState }) => {
  const isActive = row.revoked_at == null;
  const hasRaw = Boolean(row.raw_key);

  return (
    <tr className={isActive ? "" : "row-disabled"}>
      <td>
        <strong>{row.name}</strong>
        <div className="muted small">{row.key_prefix}…</div>
      </td>
      <td>
        {Array.isArray(row.scopes) && row.scopes.length > 0
          ? row.scopes.join(", ")
          : "—"}
      </td>
      <td>{formatDateTime(row.created_at)}</td>
      <td>{formatDateTime(row.last_used_at)}</td>
      <td>
        {isActive ? (
          <span style={{ color: "var(--success, #2e7d32)" }}>Aktif</span>
        ) : (
          <span className="muted">İptal · {formatDateTime(row.revoked_at)}</span>
        )}
      </td>
      <td>
        {isActive && hasRaw && (
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => onCopy(row)}
            title="Ham anahtarı panoya kopyala"
          >
            {copyState?.id === row.id && copyState?.copied
              ? "Kopyalandı ✓"
              : "Kopyala"}
          </button>
        )}
        {isActive && !hasRaw && (
          <span className="muted small">
            Anahtar daha önce kopyalandı veya temizlendi. Yeniden görmek için
            "Yeniden üret"e basın.
          </span>
        )}
        {isActive && (
          <button
            type="button"
            className="btn btn-danger"
            style={{ marginLeft: 8 }}
            onClick={() => onRevoke(row)}
          >
            İptal et
          </button>
        )}
      </td>
    </tr>
  );
};

const AdminIntegrationKeysPage = () => {
  const confirm = useConfirm();
  const { t } = useI18n();
  const [tab, setTab] = useState("active"); // active | revoked | all
  const [search, setSearch] = useState("");
  const [showRaw, setShowRaw] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusMsg, setStatusMsg] = useState("");
  const [rotating, setRotating] = useState(false);
  const [copyState, setCopyState] = useState(null);
  const [revealedRaw, setRevealedRaw] = useState(null);

  const load = async () => {
    setLoading(true);
    setStatusMsg("");
    try {
      const res = await apiClient.get("/admin/integration-api-keys");
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setStatusMsg(err.response?.data?.msg || "Anahtarlar alınamadı.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Ham anahtar ekranda uzun süre kalmasın: 2 dakika sonra otomatik gizlenir.
  useEffect(() => {
    if (!revealedRaw) return undefined;
    setShowRaw(false);
    const timer = setTimeout(() => setRevealedRaw(null), 120000);
    return () => clearTimeout(timer);
  }, [revealedRaw]);

  const tabCounts = useMemo(
    () => ({
      active: rows.filter((r) => r.revoked_at == null).length,
      revoked: rows.filter((r) => r.revoked_at != null).length,
      all: rows.length,
    }),
    [rows]
  );

  const visibleRows = useMemo(() => {
    const q = search.trim().toLocaleLowerCase("tr-TR");
    return rows.filter((r) => {
      if (tab === "active" && r.revoked_at != null) return false;
      if (tab === "revoked" && r.revoked_at == null) return false;
      if (!q) return true;
      return `${r.name || ""} ${r.key_prefix || ""} ${(r.scopes || []).join(" ")}`
        .toLocaleLowerCase("tr-TR")
        .includes(q);
    });
  }, [rows, tab, search]);

  const handleCopy = async (row) => {
    if (!row?.raw_key) return;
    try {
      await navigator.clipboard.writeText(row.raw_key);
      setCopyState({ id: row.id, copied: true });
      setTimeout(() => setCopyState(null), 2000);
    } catch {
      // Pano izni reddedilirse fallback: prompt() ile göster.
      window.prompt("Anahtarı kopyalayın:", row.raw_key);
    }
  };

  const handleRotate = async () => {
    if (rotating) return;
    const ok = await confirm(
      "Yeni bir 'gitlabeditor' anahtarı üretilecek ve mevcut aktif anahtar İPTAL edilecek. " +
        "gitlabeditor servisinin env değişkeni güncellenmezse istekleri 401 dönmeye başlar. Devam edilsin mi?"
    );
    if (!ok) return;
    setRotating(true);
    setStatusMsg("");
    try {
      const res = await apiClient.post("/admin/integration-api-keys/rotate", {
        name: "gitlabeditor",
      });
      setRevealedRaw(res.data?.raw_key || null);
      await load();
      setStatusMsg("Yeni anahtar üretildi. Aşağıdan kopyalayın.");
    } catch (err) {
      setStatusMsg(err.response?.data?.msg || "Anahtar üretilemedi.");
    } finally {
      setRotating(false);
    }
  };

  const handleRevoke = async (row) => {
    const ok = await confirm(
      `"${row.name}" (${row.key_prefix}…) anahtarı iptal edilsin mi? ` +
        "Bu anahtarı kullanan servisler 401 dönmeye başlar."
    );
    if (!ok) return;
    try {
      await apiClient.post(`/admin/integration-api-keys/${row.id}/revoke`);
      setRevealedRaw(null);
      await load();
      setStatusMsg("Anahtar iptal edildi.");
    } catch (err) {
      setStatusMsg(err.response?.data?.msg || "Anahtar iptal edilemedi.");
    }
  };

  const activeRow = useMemo(
    () => rows.find((r) => r.revoked_at == null) || null,
    [rows]
  );

  return (
    <div className="admin-settings-page">
      <div className="page-head">
        <div>
          <h2>Entegrasyon API Anahtarları</h2>
          <p className="muted">
            gitlabeditor gibi dış servisler izinyonetim'in kapasite endpoint'lerine
            <code> X-API-Key </code> header'ı ile erişir. Anahtarı kopyalayıp dış
            servisin env değişkenine tanımlayın.
          </p>
        </div>
        <div>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleRotate}
            disabled={rotating}
          >
            {rotating ? "Üretiliyor…" : "Yeniden Üret"}
          </button>
        </div>
      </div>

      {statusMsg && <div className="status-banner">{statusMsg}</div>}

      {revealedRaw && (
        <div className="card form-card" style={{ marginBottom: 16 }}>
          <h3>Yeni Anahtar</h3>
          <p className="muted small">
            Bu değeri <strong>şimdi</strong> kopyalayın ve gitlabeditor'ün
            <code> IzinYonetim__ApiKey </code> env değişkenine yazın. Aşağıdaki
            "Kopyala" da çalışır; revoke veya yeniden üretim sonrası bu değer
            kaybolur.
          </p>
          <pre
            style={{
              padding: 12,
              background: "var(--surface-2, #f5f5f5)",
              borderRadius: 6,
              wordBreak: "break-all",
              whiteSpace: "pre-wrap",
            }}
          >
            {showRaw ? revealedRaw : "•".repeat(Math.min(revealedRaw.length, 40))}
          </pre>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setShowRaw((v) => !v)}
            >
              {showRaw ? t("adminKeys.hide") : t("adminKeys.show")}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                navigator.clipboard
                  ?.writeText(revealedRaw)
                  .catch(() => window.prompt("Anahtarı kopyalayın:", revealedRaw));
              }}
            >
              Panoya Kopyala
            </button>
            <button type="button" className="btn" onClick={() => setRevealedRaw(null)}>
              {t("adminKeys.dismiss")}
            </button>
          </div>
        </div>
      )}

      {!activeRow && !loading && (
        <div className="card" style={{ padding: 16, marginBottom: 16 }}>
          <p className="muted">
            Aktif anahtar yok. "Yeniden Üret" ile bir tane oluşturabilirsiniz.
            (Backend ayağa kalkarken default bir anahtar üretmiş olmalı —
            yoksa migration 034 veya bootstrap çalışmamış olabilir.)
          </p>
        </div>
      )}

      <TabBar
        value={tab}
        onChange={setTab}
        tabs={[
          { key: "active", label: t("adminKeys.tabs.active"), count: tabCounts.active },
          { key: "revoked", label: t("adminKeys.tabs.revoked"), count: tabCounts.revoked },
          { key: "all", label: t("adminKeys.tabs.all"), count: tabCounts.all },
        ]}
      />
      <FilterBar showReset={!!search} onReset={() => setSearch("")}>
        <SearchInput value={search} onChange={setSearch} placeholder={t("adminKeys.search")} />
      </FilterBar>

      <div className="card">
        {loading ? (
          <div style={{ padding: 16 }}>Yükleniyor…</div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>İsim / Prefix</th>
                <th>Yetkiler</th>
                <th>Oluşturulma</th>
                <th>Son Kullanım</th>
                <th>Durum</th>
                <th>İşlem</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((row) => (
                <KeyRow
                  key={row.id}
                  row={row}
                  copyState={copyState}
                  onCopy={handleCopy}
                  onRevoke={handleRevoke}
                />
              ))}
            </tbody>
          </table>
        )}
        {!loading && visibleRows.length === 0 && <EmptyState message={t("adminKeys.empty")} />}
      </div>
    </div>
  );
};

export default AdminIntegrationKeysPage;
